import { ClassifierData } from 'types/classifiers';
import { mapToExpensesCreateOnTimecardDto } from 'types/expenses';
import { NamedEntityMinDto } from 'types/other';
import { TimecardCreateUpdateDto, TimecardDetails, TimecardDto } from 'types/timecards';
import { getOptionsFromClassifier } from 'utils/formUtil';
import { timecardSecondsToHours, toValueFactorOptions } from './utils/timecardUtils';

export interface TimecardFormContext {
    classifiers: Record<string, ClassifierData>;
    isTwoDecimalEntryEnabled: boolean;
    /** The author a timecard without one is shown with. */
    currentUser?: NamedEntityMinDto;
}

/** The stored timecard as the edit form starts from it. The hours are the stored seconds as shown, not rounded to the case's increment. */
export const timecardDtoToForm = (
    timecard: TimecardDetails,
    { classifiers, isTwoDecimalEntryEnabled, currentUser }: TimecardFormContext
): TimecardDto => ({
    id: timecard.id,
    description: timecard.description,
    employee: timecard.employee ? timecard.employee : (currentUser ?? null),
    employees: timecard.employee ? [timecard.employee] : [],
    matter: timecard.matter,
    isNonBillable: timecard.isNonBillable,
    preserveNonBillableHours: timecard.preserveNonBillableHours,
    multiplierTypeCode: timecard.multiplierTypeCode,
    multiplierRate: timecard.multiplierRate,
    practiceAreas: timecard.practiceAreas,
    timecardDate: timecard.timecardDate,
    billedTimeInSeconds: timecard.billedTimeInSeconds,
    billedTimeInHours: String(timecardSecondsToHours(timecard.billedTimeInSeconds ?? 0, isTwoDecimalEntryEnabled)),
    totalTimeInSeconds: timecard.totalTimeInSeconds,
    totalTimeInHours: String(timecardSecondsToHours(timecard.totalTimeInSeconds ?? 0, isTwoDecimalEntryEnabled)),
    expenses: timecard.expenses,
    matterAssignment: timecard.matterAssignment
        ? {
              id: timecard.matterAssignment.id,
              name: timecard.matterAssignment.name,
              isNonBillable: timecard.matterAssignment.isNonBillable,
              preserveNonBillableHours: timecard.matterAssignment.preserveNonBillableHours,
              matter: {
                  id: timecard.matterAssignment.matterId,
                  name: timecard.matter?.name ?? '',
                  fullName: timecard.matter?.fullName ?? '',
              },
              endDate: timecard.matterAssignment.endDate,
          }
        : null,
    timecardActivityType:
        (getOptionsFromClassifier(classifiers['TIMECARD_ACTIVITY_TYPE']) as { label: string; value: string }[]).find(
            (val) => val.value === timecard.timecardActivityTypeCode
        ) ?? null,
    remunerationCalcType: timecard.remunerationCalcTypeCode,
    remunerationTimeInSeconds: timecard.remunerationTimeInSeconds ?? 0,
    remunerationTimeInHours: String(
        timecardSecondsToHours(timecard.remunerationTimeInSeconds ?? 0, isTwoDecimalEntryEnabled)
    ),
    billingUpliftValueFactors: toValueFactorOptions(timecard.billingUpliftValueFactors, classifiers),
    valueFactorsNotes: timecard.valueFactorsNotes ?? '',
    notes: timecard.notes ?? '',
});

export type TimecardRequest = TimecardCreateUpdateDto & { languageTypeCode?: string | null };

/**
 * The create / update body for the form. Without `stored` the language is left out, and the backend sets the
 * case's. With it, the stored language goes back unless the case changed, so a save keeps a language it did not
 * touch.
 */
export const timecardFormToRequest = (
    form: TimecardDto,
    stored?: Pick<TimecardDetails, 'languageTypeCode' | 'matter'>
): TimecardRequest => ({
    id: form.id,
    billedTimeInSeconds: form?.billedTimeInSeconds ?? 0,
    description: form?.description || '',
    employee: form?.employee || null,
    isNonBillable: form?.isNonBillable || false,
    matter: form?.matter || null,
    matterAssignment: form?.matterAssignment ?? null,
    multiplierTypeCode: form?.multiplierTypeCode,
    multiplierRate: form.multiplierRate,
    practiceAreas: form.practiceAreas,
    timecardDate: form?.timecardDate,
    timecardDateUtcTimestamp: form?.timecardDate ? new Date(`${form.timecardDate}T00:00:00`).toISOString() : '',
    totalTimeInSeconds: form?.totalTimeInSeconds ?? 0,
    billedTimeInHours: form?.billedTimeInHours,
    expenses: form?.expenses,
    totalTimeInHours: form?.totalTimeInHours,
    timecardActivityTypeCode: form.timecardActivityType ? form.timecardActivityType.value : null,
    remunerationCalcTypeCode: form.remunerationCalcType ?? null,
    remunerationTimeInSeconds: form.remunerationTimeInSeconds,
    preserveNonBillableHours: form.preserveNonBillableHours,
    employees: form?.employees,
    billingUpliftValueFactors: (form.billingUpliftValueFactors ?? []).map((option) => option.value),
    valueFactorsNotes: form.valueFactorsNotes || null,
    notes: form.notes ?? null,
    ...(stored ? { languageTypeCode: form.matter?.id === stored.matter.id ? stored.languageTypeCode : null } : {}),
});

/**
 * The update body for the stored timecard with `edited` over it: the stored language unless the case changed, and
 * the expenses as the update takes them.
 */
export const timecardUpdateRequest = (edited: TimecardDto, stored: TimecardDetails) => {
    const request = timecardFormToRequest(edited, stored);
    return { ...request, expenses: mapToExpensesCreateOnTimecardDto(request.expenses ?? []) };
};

/** The form fields the record's one editable section holds: every field of the form. */
export const TIMECARD_SECTION_FIELDS = {
    timecard: [
        'matter',
        'matterAssignment',
        'timecardActivityType',
        'employee',
        'employees',
        'timecardDate',
        'practiceAreas',
        'description',
        'notes',
        'isNonBillable',
        'preserveNonBillableHours',
        'totalTimeInHours',
        'totalTimeInSeconds',
        'billedTimeInHours',
        'billedTimeInSeconds',
        'billingUpliftValueFactors',
        'valueFactorsNotes',
        'remunerationCalcType',
        'remunerationTimeInHours',
        'remunerationTimeInSeconds',
        'multiplierTypeCode',
        'multiplierRate',
        'expenses',
    ],
} satisfies Record<string, (keyof TimecardDto)[]>;
